import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, queryClient, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Layers,
  ChevronDown,
  ChevronUp,
  Send,
  X,
  PackagePlus,
  CheckCircle2,
} from "lucide-react";
import type { MaintenanceTask, Property, Vendor, TaskBatch } from "@shared/schema";

type BatchWithTasks = TaskBatch & { tasks: MaintenanceTask[] };

const BATCH_STATUS_COLORS: Record<string, string> = {
  open: "bg-slate-100 text-slate-700",
  dispatched: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-gray-100 text-gray-500",
};

function propertyLabel(properties: Property[], propertyId: number | null): string {
  if (propertyId == null) return "No property";
  const p = properties.find((pp) => pp.id === propertyId);
  return p?.name || p?.address || `Property #${propertyId}`;
}

// Task #4171 — live, color-coded age indicator for how long a job has sat in
// batch state. Thresholds track the per-host defaults (auto-demotion idle 14d,
// hold-window 30d): green while fresh, amber once it crosses the idle mark, red
// as it approaches/exceeds the hold window that resurfaces it back to "low".
function batchAge(batchedAt: string | Date | null | undefined): { label: string; cls: string } | null {
  if (!batchedAt) return null;
  const ts = new Date(batchedAt).getTime();
  if (Number.isNaN(ts)) return null;
  const days = Math.max(0, Math.floor((Date.now() - ts) / 86400000));
  const label = days === 0 ? "today" : `${days}d`;
  let cls = "bg-green-100 text-green-700";
  if (days >= 30) cls = "bg-red-100 text-red-700";
  else if (days >= 14) cls = "bg-amber-100 text-amber-800";
  return { label, cls };
}

function BatchAgeBadge({ batchedAt, testId }: { batchedAt: string | Date | null | undefined; testId: string }) {
  const age = batchAge(batchedAt);
  if (!age) return null;
  return (
    <span
      className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums ${age.cls}`}
      title="Time held in batch"
      data-testid={testId}
    >
      {age.label}
    </span>
  );
}

export function BatchPoolPanel({
  properties,
  vendors,
  readOnly,
  onTaskClick,
}: {
  properties: Property[];
  vendors: Vendor[];
  readOnly: boolean;
  onTaskClick: (taskId: number) => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<number>>(new Set());
  const [batchTitle, setBatchTitle] = useState("");
  const [dispatchBatch, setDispatchBatch] = useState<BatchWithTasks | null>(null);
  const [dispatchVendorId, setDispatchVendorId] = useState<string>("");

  const poolQuery = useQuery<MaintenanceTask[]>({
    queryKey: ["/api/app/tasks/batches/pool"],
    enabled: open,
  });
  const batchesQuery = useQuery<BatchWithTasks[]>({
    queryKey: ["/api/app/tasks/batches"],
    enabled: open,
  });

  const pool = poolQuery.data ?? [];
  const batches = batchesQuery.data ?? [];
  const activeBatches = batches.filter((b) => b.status === "open" || b.status === "dispatched");

  // Group the ungrouped pool by property so the host assembles per-property batches.
  const poolByProperty = useMemo(() => {
    const groups = new Map<number | null, MaintenanceTask[]>();
    for (const t of pool) {
      const key = t.propertyId ?? null;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(t);
    }
    return Array.from(groups.entries());
  }, [pool]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["/api/app/tasks/batches/pool"] });
    qc.invalidateQueries({ queryKey: ["/api/app/tasks/batches"] });
    queryClient.invalidateQueries({ queryKey: ["/api/app/tasks"] });
  };

  const createBatchMutation = useMutation({
    mutationFn: async (vars: { taskIds: number[]; propertyId: number | null; title: string | null }) => {
      const res = await apiRequest("POST", "/api/app/tasks/batches", vars);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Batch created", description: "Selected jobs were grouped into a batch." });
      setSelectedTaskIds(new Set());
      setBatchTitle("");
      invalidate();
    },
    onError: (err) => {
      toast({ title: "Couldn't create batch", description: parseErrorMessage(err, ""), variant: "destructive" });
    },
  });

  const dispatchMutation = useMutation({
    mutationFn: async (vars: { batchId: number; vendorId: number }) => {
      const res = await apiRequest("POST", `/api/app/tasks/batches/${vars.batchId}/dispatch`, { vendorId: vars.vendorId });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Batch dispatched", description: "The grouped work order was sent to the vendor." });
      setDispatchBatch(null);
      setDispatchVendorId("");
      invalidate();
    },
    onError: (err) => {
      toast({ title: "Couldn't dispatch batch", description: parseErrorMessage(err, ""), variant: "destructive" });
    },
  });

  const removeTaskMutation = useMutation({
    mutationFn: async (vars: { batchId: number; taskId: number }) => {
      const res = await apiRequest("POST", `/api/app/tasks/batches/${vars.batchId}/remove-task`, { taskId: vars.taskId });
      return res.json();
    },
    onSuccess: () => invalidate(),
    onError: (err) => {
      toast({ title: "Couldn't remove job", description: parseErrorMessage(err, ""), variant: "destructive" });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (batchId: number) => {
      const res = await apiRequest("POST", `/api/app/tasks/batches/${batchId}/cancel`, {});
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Batch cancelled", description: "Its jobs were returned to the batch pool." });
      invalidate();
    },
    onError: (err) => {
      toast({ title: "Couldn't cancel batch", description: parseErrorMessage(err, ""), variant: "destructive" });
    },
  });

  const toggleTask = (id: number) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const createFromSelection = () => {
    const taskIds = Array.from(selectedTaskIds);
    if (taskIds.length === 0) return;
    // Infer the property when every selected task shares one.
    const propIds = Array.from(new Set(pool.filter((t) => selectedTaskIds.has(t.id)).map((t) => t.propertyId ?? null)));
    const propertyId = propIds.length === 1 ? propIds[0] : null;
    createBatchMutation.mutate({ taskIds, propertyId, title: batchTitle.trim() || null });
  };

  const totalPool = pool.length;
  const activeVendors = vendors.filter((v) => v.status === "active");

  return (
    <div className="mb-4" data-testid="section-batch-pool">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-left transition-colors hover:bg-slate-100"
        data-testid="button-toggle-batch-pool"
      >
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-slate-600" />
          <span className="text-sm font-medium text-slate-700" data-testid="text-batch-pool-title">
            Batch Pool
          </span>
          {open && (
            <Badge className="bg-slate-200 text-slate-700" data-testid="badge-batch-pool-count">
              {totalPool} waiting · {activeBatches.length} batch{activeBatches.length === 1 ? "" : "es"}
            </Badge>
          )}
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
      </button>

      {open && (
        <div className="mt-2 space-y-4 rounded-md border border-slate-200 bg-white p-3" data-testid="panel-batch-pool">
          {(poolQuery.isLoading || batchesQuery.isLoading) ? (
            <p className="px-1 py-4 text-center text-sm text-gray-500" data-testid="text-batch-pool-loading">Loading…</p>
          ) : (
            <>
              {/* Ungrouped pool — assemble a new batch */}
              <div data-testid="subsection-batch-pool-ungrouped">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <h4 className="text-sm font-semibold text-gray-800">Deferred jobs ({totalPool})</h4>
                  {selectedTaskIds.size > 0 && (
                    <span className="text-xs text-gray-500" data-testid="text-batch-selection-count">{selectedTaskIds.size} selected</span>
                  )}
                </div>
                {totalPool === 0 ? (
                  <p className="text-sm text-gray-500" data-testid="text-batch-pool-empty">
                    No deferred jobs waiting. Set a task's priority to <span className="font-medium">Batch</span> to hold it here for grouping.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {poolByProperty.map(([propId, tasks]) => (
                      <div key={String(propId)} className="rounded-md border border-gray-100" data-testid={`group-batch-pool-${propId ?? "none"}`}>
                        <div className="border-b border-gray-100 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-600">
                          {propertyLabel(properties, propId)}
                        </div>
                        <ul className="divide-y divide-gray-100">
                          {tasks.map((t) => (
                            <li key={t.id} className="flex items-center gap-3 px-3 py-2" data-testid={`row-batch-pool-task-${t.id}`}>
                              <Checkbox
                                checked={selectedTaskIds.has(t.id)}
                                onCheckedChange={() => toggleTask(t.id)}
                                disabled={readOnly}
                                data-testid={`checkbox-batch-pool-task-${t.id}`}
                              />
                              <button
                                type="button"
                                onClick={() => onTaskClick(t.id)}
                                className="flex-1 min-w-0 text-left"
                                data-testid={`link-batch-pool-task-${t.id}`}
                              >
                                <span className="text-sm text-gray-900 truncate">{t.title}</span>
                              </button>
                              <BatchAgeBadge batchedAt={t.batchedAt} testId={`badge-batch-age-pool-${t.id}`} />
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <Input
                        value={batchTitle}
                        onChange={(e) => setBatchTitle(e.target.value)}
                        placeholder="Batch title (optional)"
                        className="sm:max-w-xs"
                        disabled={readOnly}
                        data-testid="input-batch-title"
                      />
                      <Button
                        onClick={createFromSelection}
                        disabled={readOnly || selectedTaskIds.size === 0 || createBatchMutation.isPending}
                        data-testid="button-create-batch"
                      >
                        <PackagePlus className="h-4 w-4 mr-1.5" />
                        {createBatchMutation.isPending ? "Creating…" : `Create batch (${selectedTaskIds.size})`}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Existing batches */}
              <div data-testid="subsection-batch-list">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">Batches</h4>
                {activeBatches.length === 0 ? (
                  <p className="text-sm text-gray-500" data-testid="text-batch-list-empty">No active batches yet.</p>
                ) : (
                  <div className="space-y-3">
                    {activeBatches.map((b) => {
                      const vendor = b.vendorId ? vendors.find((v) => v.id === b.vendorId) : null;
                      const completedCount = b.tasks.filter((t) => t.status === "completed" || t.status === "complete").length;
                      return (
                        <div key={b.id} className="rounded-md border border-gray-200" data-testid={`card-batch-${b.id}`}>
                          <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-sm font-semibold text-gray-900 truncate" data-testid={`text-batch-name-${b.id}`}>
                                {b.title ? `${b.title} ` : ""}#{b.number}
                              </span>
                              <Badge className={BATCH_STATUS_COLORS[b.status] ?? "bg-gray-100 text-gray-700"} data-testid={`badge-batch-status-${b.id}`}>
                                {b.status}
                              </Badge>
                              <span className="text-xs text-gray-500">{propertyLabel(properties, b.propertyId)}</span>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              {b.status === "open" && (
                                <Button
                                  size="sm"
                                  onClick={() => { setDispatchBatch(b); setDispatchVendorId(b.vendorId ? String(b.vendorId) : ""); }}
                                  disabled={readOnly || b.tasks.length === 0}
                                  data-testid={`button-dispatch-batch-${b.id}`}
                                >
                                  <Send className="h-3.5 w-3.5 mr-1" />
                                  Dispatch
                                </Button>
                              )}
                              {b.status !== "completed" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => cancelMutation.mutate(b.id)}
                                  disabled={readOnly || cancelMutation.isPending}
                                  data-testid={`button-cancel-batch-${b.id}`}
                                >
                                  Cancel
                                </Button>
                              )}
                            </div>
                          </div>
                          {b.status === "dispatched" && (
                            <div className="px-3 py-1.5 text-xs text-gray-600 border-b border-gray-100" data-testid={`text-batch-progress-${b.id}`}>
                              <span className="inline-flex items-center gap-1">
                                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                                {completedCount} of {b.tasks.length} jobs completed
                              </span>
                              {vendor && <span className="ml-2">· {vendor.name}</span>}
                            </div>
                          )}
                          <ul className="divide-y divide-gray-100">
                            {b.tasks.map((t) => (
                              <li key={t.id} className="flex items-center gap-3 px-3 py-2" data-testid={`row-batch-task-${b.id}-${t.id}`}>
                                <button
                                  type="button"
                                  onClick={() => onTaskClick(t.id)}
                                  className="flex-1 min-w-0 text-left"
                                  data-testid={`link-batch-task-${t.id}`}
                                >
                                  <span className="text-sm text-gray-900 truncate">{t.title}</span>
                                  <span className="ml-2 text-xs text-gray-500">{t.status}</span>
                                </button>
                                <BatchAgeBadge batchedAt={t.batchedAt} testId={`badge-batch-age-task-${t.id}`} />
                                {b.status === "open" && (
                                  <button
                                    type="button"
                                    onClick={() => removeTaskMutation.mutate({ batchId: b.id, taskId: t.id })}
                                    disabled={readOnly || removeTaskMutation.isPending}
                                    className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                                    title="Remove from batch"
                                    data-testid={`button-remove-batch-task-${t.id}`}
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <Dialog
        open={dispatchBatch !== null}
        onOpenChange={(o) => { if (!o && !dispatchMutation.isPending) { setDispatchBatch(null); setDispatchVendorId(""); } }}
      >
        <DialogContent
          onInteractOutside={(e) => { if (dispatchMutation.isPending) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (dispatchMutation.isPending) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (dispatchMutation.isPending) e.preventDefault(); }}
          data-testid="dialog-dispatch-batch"
        >
          <DialogHeader>
            <DialogTitle>Dispatch batch to a vendor</DialogTitle>
            <DialogDescription>
              {dispatchBatch
                ? `Send all ${dispatchBatch.tasks.length} job${dispatchBatch.tasks.length === 1 ? "" : "s"} in ${dispatchBatch.title ? `"${dispatchBatch.title}" ` : ""}Batch #${dispatchBatch.number} to one vendor as a single work order.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Select value={dispatchVendorId} onValueChange={setDispatchVendorId}>
              <SelectTrigger data-testid="select-dispatch-vendor">
                <SelectValue placeholder="Choose a vendor" />
              </SelectTrigger>
              <SelectContent>
                {activeVendors.map((v) => (
                  <SelectItem key={v.id} value={String(v.id)}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setDispatchBatch(null); setDispatchVendorId(""); }}
              disabled={dispatchMutation.isPending}
              data-testid="button-cancel-dispatch"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!dispatchBatch || !dispatchVendorId) return;
                dispatchMutation.mutate({ batchId: dispatchBatch.id, vendorId: Number(dispatchVendorId) });
              }}
              disabled={!dispatchVendorId || dispatchMutation.isPending}
              data-testid="button-confirm-dispatch"
            >
              {dispatchMutation.isPending ? "Dispatching…" : "Dispatch"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
